// Log a service on an asset (Task U): the date, what was found, and the certificate in the same step — upload it, or pick
// one already on the asset. The next service date rolls forward by the asset's interval.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal, Button, Field, Input, Select, Textarea } from '../ui';
import { formatDate } from '../../lib/viz';
import { toUpload } from '../../lib/resident';
import { assetDocsApi } from '../../lib/maintenance';

export function LogServiceDialog({ asset, open, onClose }: { asset: Record<string, any>; open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);
  const [servicedOn, setServicedOn] = useState(today);
  const [note, setNote] = useState('');
  const [mode, setMode] = useState<'upload' | 'existing' | 'none'>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [docId, setDocId] = useState('');
  const docs = useQuery({ queryKey: ['asset-docs', asset.id], queryFn: () => assetDocsApi.list(asset.id), enabled: open });
  const certs = (docs.data ?? []).filter((d) => d.category === 'SERVICE_CERT');
  const next = asset.ppmIntervalDays ? new Date(Date.parse(servicedOn) + asset.ppmIntervalDays * 86_400_000).toISOString().slice(0, 10) : null;
  const save = useMutation({
    mutationFn: async () => assetDocsApi.logService(asset.id, {
      servicedOn, note: note.trim() || null,
      certificate: mode === 'upload' && file ? await toUpload(file) : null,
      certificateDocumentId: mode === 'existing' && docId ? docId : null,
    }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['asset', asset.id] }); void qc.invalidateQueries({ queryKey: ['asset-docs', asset.id] }); void qc.invalidateQueries({ queryKey: ['/assets'] }); onClose(); },
  });
  return (
    <Modal open={open} onClose={onClose} title={`Log a service — ${asset.name}`}
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button disabled={save.isPending || (mode === 'upload' && !file) || (mode === 'existing' && !docId)} onClick={() => save.mutate()}>{save.isPending ? 'Saving…' : 'Log service'}</Button></>}>
      <div className="space-y-3">
        <Field label="Serviced on"><Input type="date" value={servicedOn} max={today} onChange={(e) => setServicedOn(e.target.value)} className="w-full" /></Field>
        <p className="text-[12.5px] text-ink-500">{next ? <>Next service moves to <b>{formatDate(next)}</b> (every {asset.ppmIntervalDays} days).</> : 'No service interval on this asset — the next date is not set.'}</p>
        <Field label="What was done / found"><Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="w-full" /></Field>
        <Field label="Service certificate">
          <Select value={mode} onChange={(e) => setMode(e.target.value as typeof mode)} className="w-full">
            <option value="upload">Upload it now</option>
            <option value="existing" disabled={!certs.length}>One already on the asset{certs.length ? '' : ' (none yet)'}</option>
            <option value="none">No certificate</option>
          </Select>
        </Field>
        {mode === 'upload' && <input type="file" accept="application/pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] ?? null)} className="block w-full text-[12.5px] text-ink-600 file:mr-3 file:rounded-lg file:border-0 file:bg-ink-100 file:px-3 file:py-1.5 file:text-[12px] file:font-medium file:text-ink-800 hover:file:bg-ink-200" />}
        {mode === 'existing' && <Select value={docId} onChange={(e) => setDocId(e.target.value)} className="w-full"><option value="">Choose…</option>{certs.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}</Select>}
        {save.isError && <p className="text-xs text-red-600">{(save.error as Error).message}</p>}
      </div>
    </Modal>
  );
}
